import React from "react";
import { Box, Typography, Button, Container, useTheme } from "@mui/material";
import { styled } from "@mui/system";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

/* —— Netflix tokens —— */
const NFX = {
  bg: "#0b0b0b",
  panel: "#141414",
  text: "#ffffff",
  textMuted: "rgba(255,255,255,0.75)",
  border: "rgba(255,255,255,0.12)",
  red: "#E50914",
  redHover: "#b20710",
};

const GradientBg = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  width: "100vw",
  // Vignette + subtle red glow
  background:
    "radial-gradient(1200px 520px at 10% -10%, rgba(229,9,20,0.12), transparent 60%), linear-gradient(180deg, #0b0b0b 0%, #000 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "Lexend, sans-serif",
  color: NFX.text,
  [theme.breakpoints.down("sm")]: {
    alignItems: "flex-start",
    paddingTop: theme.spacing(6),
  },
}));

const Card = styled(motion(Box))(({ theme }) => ({
  background: "linear-gradient(180deg, rgba(20,20,20,0.92), rgba(20,20,20,0.86))",
  borderRadius: 24,
  border: `1px solid ${NFX.border}`,
  boxShadow: "0 24px 60px rgba(0,0,0,0.6)",
  padding: theme.spacing(6, 5),
  maxWidth: 520,
  width: "100%",
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  marginLeft: "auto",
  marginRight: "auto",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(3, 2.25),
    maxWidth: 340,
    minWidth: 0,
    borderRadius: 18,
  },
}));

const GradientTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 900,
  fontSize: "2.4rem",
  letterSpacing: "-0.02em",
  color: NFX.text,
  textShadow: "0 2px 0 rgba(0,0,0,0.35)",
  marginBottom: theme.spacing(1.5),
  fontFamily: "Lexend, sans-serif",
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.6rem",
    marginBottom: theme.spacing(1),
  },
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  color: NFX.text,
  fontWeight: 800,
  fontSize: "1.1rem",
  marginBottom: theme.spacing(1.5),
  letterSpacing: "-0.01em",
  [theme.breakpoints.down("sm")]: {
    fontSize: "1rem",
    marginBottom: theme.spacing(1),
  },
}));

const Description = styled(Typography)(({ theme }) => ({
  color: NFX.textMuted,
  fontSize: "1.02rem",
  lineHeight: 1.6,
  marginBottom: theme.spacing(3.5),
  maxWidth: 420,
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.96rem",
    marginBottom: theme.spacing(2.25),
  },
}));

const ResponsiveButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  fontWeight: 900,
  fontFamily: "Lexend, sans-serif",
  padding: theme.spacing(1.4, 3.5),
  textTransform: "none",
  fontSize: "1rem",
  backgroundColor: NFX.red,
  color: "#fff",
  boxShadow: "0 14px 32px rgba(229,9,20,0.35)",
  transition: "transform .15s ease, box-shadow .15s ease, background .15s ease",
  "&:hover": {
    backgroundColor: NFX.redHover,
    transform: "translateY(-1px)",
    boxShadow: "0 18px 40px rgba(229,9,20,0.45)",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    fontSize: "0.98rem",
    padding: theme.spacing(1.15, 0),
  },
}));

const ComingSoon: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <GradientBg>
      <Container disableGutters sx={{ px: { xs: 1.25, sm: 0 } }}>
        <Card
          initial={{ opacity: 0, y: 36 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <GradientTitle>Coming Soon</GradientTitle>

          <Subtitle>We’re crafting something binge-worthy.</Subtitle>

          <Description>
            Our new experience is almost here. Stay tuned and get ready to join the next
            generation of talent and opportunity on <strong>StageLink</strong>.
          </Description>

          <ResponsiveButton
            variant="contained"
            size="large"
            onClick={() => navigate("/home")}
          >
            Back to Home
          </ResponsiveButton>
        </Card>
      </Container>
    </GradientBg>
  );
};

export default ComingSoon;
